import { db } from './db.js';

/**
 * El descanso que está corriendo ahora mismo (UX-002).
 *
 * POR QUÉ SE GUARDA
 *
 * Vivía solo en el estado de React, así que salir de la sesión —mirar el plan,
 * consultar el «¿Por qué?» de otra pantalla, o que Android descargue la
 * pestaña— lo borraba. Al volver no había descanso: ni cuenta atrás, ni aviso,
 * y ninguna forma de saber cuánto llevabas parado. Entre series eso no es un
 * detalle, es la única información que hay en pantalla.
 *
 * Se guarda el INSTANTE en el que arrancó, no lo que queda. Lo que queda se
 * calcula al leerlo, que es lo que hace que el tiempo pasado fuera cuente igual
 * que el tiempo pasado delante. Guardar el restante obligaría a actualizarlo
 * mientras la pantalla no existe, que es justo lo que no se puede hacer.
 *
 * Vive en `meta`, que es local y no sincroniza. Un descanso a medias no es un
 * dato del usuario: es estado de ESTE dispositivo, y mandarlo al otro haría
 * aparecer allí una cuenta atrás de una sesión que no se está haciendo.
 */

const CLAVE = 'descanso-activo';

export interface DescansoActivo {
  workoutLogId: string;
  setId: string;
  segundos: number;
  /** Marca temporal (ms) en la que arrancó. */
  desde: number;
}

function esDescanso(value: unknown): value is DescansoActivo {
  if (typeof value !== 'object' || value === null) return false;
  const fila = value as Partial<DescansoActivo>;
  return (
    typeof fila.workoutLogId === 'string' &&
    typeof fila.setId === 'string' &&
    typeof fila.segundos === 'number' &&
    typeof fila.desde === 'number'
  );
}

export async function guardarDescanso(descanso: DescansoActivo): Promise<void> {
  await db.meta.put({ key: CLAVE, value: descanso });
}

/**
 * El descanso guardado, si sigue teniendo sentido.
 *
 * Devuelve `null` cuando es de OTRO parte de entrenamiento o cuando ya se pasó
 * de largo. Lo segundo importa: al volver dos horas después no se puede pintar
 * un descanso terminado hace hora y media como si acabara de acabar. Se da un
 * margen —el propio descanso otra vez— para que volver justo al terminar siga
 * enseñando el «¡Vamos!».
 */
export async function leerDescanso(
  workoutLogId: string,
  ahora: number = Date.now(),
): Promise<DescansoActivo | null> {
  const fila = await db.meta.get(CLAVE);
  if (fila === undefined || !esDescanso(fila.value)) return null;

  const descanso = fila.value;
  if (descanso.workoutLogId !== workoutLogId) return null;

  const caduca = descanso.desde + descanso.segundos * 2 * 1000;
  return ahora <= caduca ? descanso : null;
}

export async function olvidarDescanso(): Promise<void> {
  await db.meta.delete(CLAVE);
}
